import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';
import type {
  AdminAccountView,
  AdminDeleteResult,
  AdminRole,
  CreateAdminInput,
  CreatedAdminAccount,
} from '@shared/schemas/admin-account';

/* The queries behind /admin/admins — business-rules.md §2. */

const ROOT = ['admin', 'admin-accounts'] as const;

export function useAdminAccounts() {
  return useQuery({
    queryKey: ROOT,
    queryFn: () => api.get<{ data: AdminAccountView[] }>('/api/admin/admin-accounts'),
  });
}

/** The recycle bin — deleted, not yet purged. Owner decision, 2026-08-07. */
export function useDeletedAdminAccounts() {
  return useQuery({
    queryKey: [...ROOT, 'deleted'],
    queryFn: () => api.get<{ accounts: AdminAccountView[] }>('/api/admin/admin-accounts/deleted'),
  });
}

/**
 * Delete an account.
 *
 * The result says which of the two things happened — gone, or recoverable for
 * 30 days — because the toast has to tell the truth about whether Undo exists.
 */
export function useDeleteAdminAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete<AdminDeleteResult>(`/api/admin/admin-accounts/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ROOT }),
  });
}

export function useRestoreAdminAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.post<AdminAccountView>(`/api/admin/admin-accounts/${id}/restore`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ROOT }),
  });
}

export function useCreateAdminAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateAdminInput) =>
      api.post<CreatedAdminAccount>('/api/admin/admin-accounts', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ROOT }),
  });
}

export function useChangeAdminRole(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (role: AdminRole) =>
      api.patch<AdminAccountView>(`/api/admin/admin-accounts/${id}/role`, { role }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ROOT }),
  });
}

export function useSetAdminAccountActive(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (isActive: boolean) =>
      api.post<AdminAccountView>(
        `/api/admin/admin-accounts/${id}/${isActive ? 'reactivate' : 'deactivate'}`,
        {},
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ROOT }),
  });
}
